import React, { useMemo, useRef, useEffect } from 'react';
import { SHOPIFY_PRODUCT_LINK } from '../../../constants/SocialMediaLinks';
import useResize from '../hooks/useResize';
import useViewMoreModal from '../hooks/useViewMoreModal';

const ProductsSeenInVideo = ({
	products = [],
	headerText = '',
	titleStyles,
	visible = 0,
	contentType = '',
	customeStyles = {}
}) => {
	if (!products?.length) return null;

	const uniqueProducts = useMemo(() => {
		const byProduct = new Map();
		products.forEach(product => {
			const key = product?._id || product?.id || product?.handle || product?.title;
			if (key && !byProduct.has(key)) byProduct.set(key, product);
		});
		return [...byProduct.values()];
	}, [products]);

	const visibleCount = visible || 2;
	const { ViewMoreButton, Modal } = useViewMoreModal(contentType);
	const deviceType = useResize();
	const isMobile = deviceType === 'mobile';

	const listRef = useRef(null);

	// Calculate capacity based on container width and fixed item width (200px + 16px gap)
	const [capacity, setCapacity] = React.useState(0);

	useEffect(() => {
		const calculateCapacity = () => {
			if (!listRef.current) return;
			const containerWidth = listRef.current.getBoundingClientRect().width;
			const itemWidth = 200; // Fixed width from (based on) CSS
			const gap = 16; // Column gap from (based on) CSS
			const perRow = Math.max(1, Math.floor((containerWidth + gap) / (itemWidth + gap)));
			setCapacity(perRow);
		};

		calculateCapacity();

		// Recalculate on resize
		window.addEventListener('resize', calculateCapacity);

		// Use ResizeObserver for container size changes
		const observer = new ResizeObserver(calculateCapacity);
		if (listRef.current) {
			observer.observe(listRef.current);
		}

		return () => {
			window.removeEventListener('resize', calculateCapacity);
			observer.disconnect();
		};
	}, []);

	// Use capacity from calculation, or fallback to visible prop
	const itemsToShow = capacity > 0 ? Math.min(capacity, uniqueProducts.length) : visibleCount;

	const desktopSlice = useMemo(() => uniqueProducts.slice(0, itemsToShow), [uniqueProducts, itemsToShow]);

	const renderList = isMobile ? uniqueProducts : desktopSlice;

	// Check if there are more items than can fit
	const shouldShowViewAll = uniqueProducts.length > capacity && capacity > 0;

	return (
		<div className="c-video-set-list-product c-mobile-video_slider" style={{ ...customeStyles }}>
			<div className="c-video-set-list-h3">
				{!isMobile && <h3 style={{ ...titleStyles }}>{headerText || 'PRODUCTS SEEN IN VIDEO'}</h3>}
				<ViewMoreButton shouldShow={shouldShowViewAll} />
			</div>

			<div className="c-video-set-listbox" ref={listRef}>
				{renderList.map(product => (
					<div key={product?._id || product?.id || product?.handle || product?.title} className="c-video_description-sideimg">
						<img src={product?.images?.[0]?.src} alt="Not found" style={{ aspectRatio: '1 / 1' }} />
						<a
							href={`${SHOPIFY_PRODUCT_LINK}/${product?.handle}${product?.variantId ? `?variant=${product.variantId}` : ''}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							BUY NOW
						</a>

					</div>
				))}
			</div>

			<Modal products={uniqueProducts} />
		</div>
	);
};

export default ProductsSeenInVideo;
